/**
 * First-run empty states on Bill's list pages, base (legal) dialect.
 *
 * A page with no data of its own shows a useful intro where needed, a
 * checklist of setup steps derived from what the account already has, and an
 * Ask Bill card. `steps` holds each setup step's copy; `pages` holds each
 * page's intro and reassurance. Which step is next is decided in the app
 * (`apps/web/bill/src/components/empty-state/empty-state.ladders.ts`), so a
 * step added there needs its copy added here, and the reverse.
 *
 * Ask Bill prompts are SENT to the chat agent as soon as they are clicked, so
 * each one must be a request the agent can act on (it asks for missing details)
 * or a question its help docs answer (`docs/help`, read through `getHelpDoc`).
 * Never put an example name in an action prompt: the agent would create it.
 * `{{client}}` in a prompt is the account's own client, never an example.
 *
 * Product claims here were checked against code and help docs; the read-only
 * lines restate the OAuth scopes (`gmail.readonly`, `Mail.Read`,
 * `calendar.readonly`, `Calendars.Read` in the backend's oauth providers), so a
 * write scope falsifies them and must change them in the same change. IMAP is
 * not claimed read-only.
 */
export const emptyState = {
    /** A step that waits on an earlier one. Interpolates {{noun}} (a step's `noun`). */
    needs: 'Needs {{noun}}',
    /** The accessible name of the setup checklist. */
    checklistLabel: 'Steps to get started',
    askBill: {
        title: 'Ask Bill',
        inputLabel: 'Ask Bill anything',
        placeholder: 'Ask a question...',
        send: 'Send to Bill',
    },
    steps: {
        client: {
            noun: 'a client',
            title: 'Add a client',
            doneMany: 'Clients added',
            next: {
                title: 'Add a client',
                body: 'Add their email address to match emails and meetings.',
            },
            action: 'Add a client',
            ask: {
                add: 'Help me add my first client',
                import: 'How do I import my clients from a spreadsheet?',
            },
        },
        matter: {
            noun: 'a matter',
            title: 'Open a matter',
            doneMany: 'Matters open',
            next: {
                /** One client: the matter opens for them. Interpolates {{client}}. */
                titleOne: 'Open a matter for {{client}}',
                bodyOne: 'Time and expenses are recorded against a matter.',
                titleMany: 'Open a matter',
            },
            action: 'Open a matter',
            ask: {
                /** Interpolates {{client}}, the account's only client. */
                helpOne: 'Help me open a matter for {{client}}',
                helpMany: 'Help me open my first matter',
                what: 'What is a matter in Bill?',
            },
        },
        activity: {
            noun: 'an activity',
            title: 'Log an activity',
            done: 'Activities recorded',
            next: {
                title: 'Log an activity',
                /** One matter: the form opens with it selected. Interpolates {{matter}}. */
                bodyOne: 'Record time for {{matter}}.',
                bodyMany: 'Log time against any of your matters.',
            },
            action: 'Log activity',
            ask: {
                log: 'Help me log time I worked today',
                whatsApp: 'How do I log time over WhatsApp?',
                fromEmail: 'Can Bill draft time from my emails?',
            },
        },
        approve: {
            noun: 'approved work',
            title: 'Approve work',
            /** Interpolates {{amount}}, formatted (e.g. R3,750.00). */
            done: '{{amount}} approved',
            next: {
                title: 'Approve draft work',
            },
            action: 'Review drafts',
            ask: {
                which: 'Which draft activities need approving?',
                how: 'How do I approve activities?',
            },
        },
        invoice: {
            noun: 'an invoice',
            title: 'Create an invoice',
            next: {
                /** Approved, uninvoiced work exists. Interpolates {{amount}}, formatted (e.g. R3,750.00). */
                titleReady: '{{amount}} of approved work is ready to bill',
                bodyReady: 'Bill adds it to a draft invoice for you to check.',
                /** Under titleReady when draft work also waits; text only, the row keeps one button. */
                draftsWaiting: 'Draft work also needs approval.',
                /** An administered entity's books, which have no activities to approve. */
                title: 'Create an invoice',
                body: 'Start with a blank draft. Add the details and approve it before sending.',
            },
            action: 'Create invoice',
            blankAction: 'Create blank invoice',
            ask: {
                ready: 'What work is ready to bill?',
                how: 'How do invoices work in Bill?',
            },
        },
        inbox: {
            noun: 'a connected inbox',
            title: 'Connect your inbox',
            next: {
                title: 'Connect your inbox',
                body: 'Connect Gmail, Outlook or IMAP. Bill reads your emails but never sends anything.',
            },
            action: 'Connect inbox',
            ask: {
                how: 'How does Bill turn emails into draft activities?',
                safe: 'Is my inbox safe with Bill?',
            },
        },
        calendar: {
            noun: 'a connected calendar',
            title: 'Connect your calendar',
            next: {
                title: 'Connect your calendar',
                body: 'Connect Google or Microsoft. Bill reads your calendar events without changing them.',
            },
            action: 'Connect calendar',
            ask: {
                which: 'Which meetings will Bill draft time for?',
                safe: 'Is my calendar safe with Bill?',
            },
        },
        expense: {
            noun: 'an expense',
            title: 'Add an expense',
            next: {
                title: 'Add an expense',
                body: "Upload a supplier's invoice and Bill reads the details.",
            },
            action: 'Add expense',
            ask: {
                billOn: "How do I bill an Advocate's fee to my client?",
                read: "What can Bill read from a supplier's invoice?",
            },
        },
    },
    pages: {
        activities: {
            intro: 'Record time against matters, then approve it for invoicing.',
        },
        invoices: {
            intro: 'Create a blank invoice or bill approved time and expenses.',
            reassurance: 'Invoices stay in draft until you approve them.',
        },
        emails: {
            intro: 'Turn client emails into draft activities.',
            /** The emails list is showing, but no client exists to match them to. */
            strip: {
                title: 'Add a client so Bill can match their emails.',
                body: 'Bill matches emails to clients by their email address.',
            },
        },
        calendar: {
            intro: 'Turn client meetings into draft activities.',
            /** The events list is showing, but no client exists to match them to. */
            strip: {
                title: 'Add a client so Bill can match their meetings.',
                body: 'Bill matches meetings to clients by the attendees.',
            },
        },
        expenses: {
            intro: "Record expenses and bill them to your clients.",
        },
    },
    suppliers: {
        title: 'No suppliers',
        description: 'Suppliers are added as you record expenses, or you can add one here.',
        primaryAction: 'Add supplier',
    },
    activities: {
        /** The in-card state on a matter's own page, where the card header already offers Log activity. */
        projectTitle: 'No activities recorded for this matter',
    },
};
